import type { BuyerActionsResponse } from '../types';

import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysCallOff } from '../queryKeys';

import { fetchBuyerActions } from './fetchBuyerActions';

/**
 * Generates the query options for the buyer actions query.
 *
 * @param callOffId - The ID of the call-off to fetch actions for.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getBuyerActionsQueryOptions = <TData = BuyerActionsResponse>({
  callOffId,
  options,
}: {
  callOffId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<BuyerActionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: callOffId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchBuyerActions(callOffId, { signal })
      : skipToken,
    queryKey: queryKeysCallOff.getBuyerActions(callOffId ?? ''),
    staleTime: ms('30s'),
    ...options,
  });

/**
/**
 * Custom React Query hook to fetch available actions a buyer can take for a given call-off.
 *
 * Uses the callOffId to determine which actions are available to the buyer based on the current call-off state.
 *
 * @param callOffId - The unique identifier of the call-off for which to fetch available buyer actions.
 * @param options - (Optional) Additional react-query options for customizing the query behavior.
 * @returns The react-query result including data, loading, and error states.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Off-Magic-Link/operation/get_available_buyer_actions_calloffs__calloff_id__magic_link_actions_get
 */
export const useQueryBuyerActions = (
  callOffId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<BuyerActionsResponse, Error, BuyerActionsResponse>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getBuyerActionsQueryOptions<BuyerActionsResponse>({
      callOffId,
      options,
    }),
  );
